import React, {useState} from 'react';
import clsx from 'clsx';
import {Avatar, type AvatarProps} from '@mui/material';
import {Icon, faPlus, faQuestion, faUser} from '@netx/core-icons';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {
	getRecognitionFaceAvatarUtilityClass,
	RECOGNITION_FACE_AVATAR_CLASS_NAME,
	type RecognitionFaceAvatarClasses,
	recognitionFaceAvatarClasses,
} from './recognitionFaceAvatarClasses';
import {splitEventHandler} from '@netx/core-next/utils';

type RecognitionFaceAvatarOwnerState = WithClasses<
	RecognitionFaceAvatarClasses,
	{}
>;

const useUtilityClasses = (state: RecognitionFaceAvatarOwnerState) => {
	const {classes} = state;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getRecognitionFaceAvatarUtilityClass, classes);
};

const RecognitionFaceAvatarRoot = styled(Avatar, {
	name: RECOGNITION_FACE_AVATAR_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<RecognitionFaceAvatarOwnerState>) => ({
	border: `1px solid ${theme.palette.custom.border}`,
}));

export type RecognitionFaceAvatarSize = 'small' | 'medium' | 'large';

export interface RecognitionFaceAvatarProps extends WithClasses<
	RecognitionFaceAvatarClasses,
	Omit<AvatarProps, 'src' | 'children'>
> {
	avatarUrl?: string | null;
	size?: RecognitionFaceAvatarSize;
	fill?: boolean;
	loading?: boolean;
	isNew?: boolean;
	showUnidentified?: boolean;
}

const sizeMapping: Record<RecognitionFaceAvatarSize, number> = {
	small: 42,
	medium: 92,
	large: 120,
};

export const RecognitionFaceAvatarPrimitive = (
	{
		classes: _classes,
		className,
		avatarUrl,
		loading,
		isNew,
		showUnidentified,
		size = 'small',
		fill = false,
		...props
	}: RecognitionFaceAvatarProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const [fallback, setFallback] = useState(false);

	const ownerState: RecognitionFaceAvatarOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const _fallback =
		loading || showUnidentified || isNew || fallback || !avatarUrl;

	return (
		<RecognitionFaceAvatarRoot
			alt={'avatar'}
			className={clsx(classes.root, className)}
			ref={ref}
			{...props}
			ownerState={ownerState}
			src={_fallback ? undefined : avatarUrl}
			children={
				_fallback ? (
					<Icon
						icon={isNew ? faPlus : showUnidentified ? faQuestion : faUser}
					/>
				) : undefined
			}
			imgProps={{
				...props.imgProps,
				onError: splitEventHandler(props.imgProps?.onError, () =>
					setFallback(true),
				),
			}}
			sx={{
				width: fill ? '100%' : sizeMapping[size] - (_fallback ? 2 : 0),
				height: fill ? '100%' : sizeMapping[size] - (_fallback ? 2 : 0),
				...props.sx,
			}}
		/>
	);
};

export const RecognitionFaceAvatar = React.forwardRef<
	HTMLDivElement,
	RecognitionFaceAvatarProps
>(RecognitionFaceAvatarPrimitive);

export default RecognitionFaceAvatar;
